import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth, passesAccess, FullPageLoader } from "@livingsyncs/ui";
import type { RouteAccess } from "@livingsyncs/ui";

interface RouteGuardProps {
  access?: RouteAccess;
  children: ReactNode;
}

/**
 * Per-route access guard, admin's own copy of frontend's `RouteGuard.tsx`
 * (frontend split epic Phase 5). Trimmed: no `shiftRequired`/`shiftGate`
 * delegation — that's a group-home staff on-shift concept
 * (`ShiftRequiredRoute`, `useFacilityContext`) admin.portal.ts never sets,
 * so there's nothing to inject here. Not moved to @livingsyncs/ui, same
 * reason as frontend's own copy: its `useAuth()` call would resolve to
 * packages/ui's own useAuth.ts instead of this app's local (currently
 * nonexistent) mock target — moot today since admin has no tests mocking
 * `@/hooks/useAuth` yet, but keeping this per-app avoids relitigating that
 * bug the moment one is added, consistent with frontend/gateway's own
 * RouteGuard/useAuth precedent.
 */
export function RouteGuard({ access, children }: RouteGuardProps) {
  const { data: context, isLoading } = useAuth();

  const needsPermissionDecision = !!(
    access?.anyOf ||
    access?.allOf ||
    access?.resource
  );

  if (needsPermissionDecision) {
    if (isLoading || !context) {
      return <FullPageLoader />;
    }
    if (!passesAccess({ permissions: context.permissions }, access)) {
      return (
        <Navigate
          to="/no-access"
          replace
          state={{
            requiredPermissions: [
              ...(access?.anyOf ?? []),
              ...(access?.allOf ?? []),
              ...(access?.resource ? [`${access.resource}:read`] : []),
            ],
            from: window.location.pathname,
          }}
        />
      );
    }
  }

  return <>{children}</>;
}
